/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
import React, { useState } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import wizardStyle from "@/src/components/Form/wizardStyle";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import FormSubmitProps from "@/src/types/FormSubmitProps";

import { Box, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { UpdateResourceParams } from "@/src/services/reactQuery/reactQueryService";
import { UseMutationResult } from "@tanstack/react-query";
import { translate } from "@/src/services/translations/translations";
import { requiredValidator } from "@/src/services/validationRules";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";

type RefinementQuestionsProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>;
};

export default function RefinementQuestions({ submitProps, product, updateMutation }: RefinementQuestionsProps) {
  const { handleSubmit, values } = useForm();
  const [isDisabled, setIsDisabled] = useState(false);

  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDisabled(true);
    updateMutation.mutateAsync({
      uri: "/pending_cases",
      id: product.id,
      data: { aiRefinementQuestions: null, refinementAnswers: null },
    });
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ergänzende Informationen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Ergänzende Informationen</strong>
              </FormLegend>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <AlertLegalbird
                severity="info"
                sx={{
                  mt: "1rem",
                  mb: "2rem",
                  color: (theme) => theme.palette.info.dark,
                }}
              >
                <AlertContentLegalbird
                  title={"Bitte lassen Sie uns noch folgende Informationen zukommen:"}
                  description={<Box dangerouslySetInnerHTML={{ __html: product.aiRefinementQuestions }} />}
                />
              </AlertLegalbird>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="refinementAnswers"
                label={translate(`pendingCase.refinementAnswers.label`, product)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  maxLength: 5000,
                }}
                multiline
                rows={10}
                validators={[requiredValidator("Bitte ergänzen Sie die fehlenden Informationen.")]}
                helperText={
                  "[" +
                  (values.refinementAnswers?.length ?? 0) +
                  "/5000]" +
                  ((values.refinementAnswers?.length ?? 0) < 50 ? " Bitte geben Sie mindestens 50 Buchstaben ein" : "")
                }
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} disabled={isDisabled} onBackClick={handleBackClick} />
        </Box>
      </FormFader>
    </Paper>
  );
}
